import {
  getLocalTimeZone,
  parseDate,
  Time,
  today
} from '@internationalized/date';
import { TimeValue } from '@goodneighbor/api/schemas/time';
import type { DateValue } from 'react-aria-components';

export const formatEventDate = (
  dateString: string,
  timeZoneId?: string | null
) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: timeZoneId || 'UTC'
  });
};

export const formatShortDate = (
  dateString: string | null | undefined,
  timeZoneId?: string | null
) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone: timeZoneId || 'UTC'
  });
};

export const formatEventTime = (
  dateString: string,
  timeZoneId?: string | null,
  options?: Intl.DateTimeFormatOptions
) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timeZoneId || 'UTC',
    ...options
  });
};

export const toUtcBoundary = (
  dateString: string,
  boundary: 'start' | 'end'
) => {
  if (!dateString) {
    return undefined;
  }

  const trimmedValue = dateString.trim();
  if (!trimmedValue) {
    return undefined;
  }

  const boundaryTime = boundary === 'start' ? 'T00:00:00.000' : 'T23:59:59.999';
  let parsedDate = new Date(trimmedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    parsedDate = new Date(`${trimmedValue}${boundaryTime}`);
  }

  if (Number.isNaN(parsedDate.getTime())) {
    return undefined;
  }

  const boundaryDate = new Date(
    Date.UTC(
      parsedDate.getUTCFullYear(),
      parsedDate.getUTCMonth(),
      parsedDate.getUTCDate(),
      boundary === 'start' ? 0 : 23,
      boundary === 'start' ? 0 : 59,
      boundary === 'start' ? 0 : 59,
      boundary === 'start' ? 0 : 999
    )
  );

  return boundaryDate.toISOString();
};

export const dateTimeStringToDate = (value: string | null) =>
  value ? new Date(value) : null;

export const normalizeDateString = (value: string | null) => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const [datePart] = trimmed.split('T');
  return datePart || null;
};

export const toCalendarDateValue = (value: string | null | undefined) => {
  const normalizedValue = normalizeDateString(value ?? null);
  if (!normalizedValue) {
    return null;
  }

  try {
    return parseDate(normalizedValue);
  } catch {
    return null;
  }
};

export const toDateString = (value: DateValue | null) => {
  if (!value) {
    return null;
  }

  const [datePart] = value.toString().split('T');
  return datePart || value.toString();
};

export const toTimeValue = (time: TimeValue | null | undefined) => {
  if (!time) {
    return null;
  }
  return new Time(time.hour, time.minute, time.second, time.millisecond);
};

export const getTodayDateString = () => today(getLocalTimeZone()).toString();

export const calculateAge = (birthDate: string | null | undefined) => {
  if (!birthDate) return '—';
  const today = new Date();
  const birth = new Date(birthDate);
  if (Number.isNaN(birth.getTime())) return '—';

  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};
